import { NotificationType } from "@/config/notification";
import { TETHER_SYMBOL } from "@/constants";
import { PushNotificationSeverity } from "@prisma/client";
import { validate } from "multicoin-address-validator";
import { z } from "zod";
import { addressSchema } from "../wallet/address-schema";

const urlSchema = z.object({
  value: z.string().url(),
});

const walletSchema = z.object({
  id: z.string().cuid(),
  addresses: z.array(addressSchema),
});

export const userSchema = z.object({
  id: z.string().cuid().optional(),
  name: z
    .string()
    .nullish()
    .transform((val) => val ?? "Anonymous"),
  email: z.string().optional(),
  emailVerified: z.date().nullish(),
  image: z
    .string()
    .url()
    .nullish()
    .transform(
      (val) => val ?? `${process.env.NEXT_PUBLIC_BASE_URL}/anonymous-dark.svg`,
    ),
  bio: z.string().nullish(),
  urls: z
    .array(urlSchema)
    .optional()
    .transform((val) => val ?? []),
  // portfolios: z.array(portfolioSchema).(),
  // watchlist: z.array(portfolioSchema).(),
  // transactions: z.array(assetTransactionSchema).(),
});

export const userSessionSchema = z.object({
  id: z.string().cuid(),
  name: z
    .string()
    .nullish()
    .transform((val) => val ?? "Anonymous"),
  bio: z
    .string()
    .nullish()
    .transform((val) => val ?? ""),
  image: z
    .string()
    .url()
    .nullish()
    .transform(
      (val) => val ?? `${process.env.NEXT_PUBLIC_BASE_URL}/anonymous-dark.svg`,
    ),

  email: z.string().email(),
  urls: z
    .array(
      z.object({
        value: z.string().url(),
      }),
    )
    .optional()
    .transform((val) => val ?? ([] as User["urls"])),
  following: z
    .array(
      z.object({
        followingId: z.string().cuid(),
      }),
    )
    .optional()
    .default([]),
  followers: z
    .array(
      z.object({
        followerId: z.string().cuid(),
      }),
    )
    .optional()
    .default([]),
  pushPreferences: z
    .object({
      enabled: z.boolean(),
      follow: z.boolean(),
      mention: z.boolean(),
      repost: z.boolean(),
      comment: z.boolean(),
      reaction: z.boolean(),
    })
    .nullish(),

  wallet: walletSchema.nullish(),
});

export const notificationTypeSchema = z.union([
  z.literal("follow"),
  z.literal("portfolio-change"),
  z.literal("message"),
  z.literal("subscribe"),
  z.literal("subscription-success"),
  z.literal("portfolio-import-done"),
]);

export const UserNotificationsSchema = z.object({
  createdAt: z.coerce.date(),
  type: notificationTypeSchema,
  readAt: z.date().nullish(),
  sender: userSchema.pick({
    id: true,
    name: true,
    image: true,
  }),
  payload: z.object({
    body: z.string(),
    data: z.object({ url: z.string().url() }),
    icon: z.string(),
    title: z.string(),
  }),
});
const userSearchItemSchema = userSchema
  .pick({
    id: true,
    name: true,
    image: true,
  })
  .required();
export const userSearchSchema = z.array(userSearchItemSchema);

export const userLastReadNotificationsSchema = z.object({
  hasUnread: z.boolean().default(false),
  notifications: z
    .array(
      UserNotificationsSchema.pick({ type: true }).extend({
        senderId: z.string().cuid(),
        userId: z.string().cuid(),
        severity: z.nativeEnum(PushNotificationSeverity),
      }),
    )
    .transform((arr) => {
      return arr.reduce((grouped, notification) => {
        // Lowe severity notifications like messages will be attached to the sender avatars
        const isLowSeverity =
          notification.severity === PushNotificationSeverity.LOW;
        const key = isLowSeverity ? notification.senderId : notification.userId;
        const item = grouped.get(key);

        if (item) {
          const count = item.get(notification.type) ?? 0;
          item.set(notification.type, count + 1);
        } else {
          const map = new Map<NotificationType, number>();
          map.set(notification.type, 1);
          grouped.set(key, map);
        }

        return grouped;
      }, new Map<string, Map<NotificationType, number>>());
    }),
});

export type UserNotification = z.infer<typeof UserNotificationsSchema>;
export type UserSearchItem = z.infer<typeof userSearchItemSchema>;

export const userIsFollowingSchema = z.object({
  isFollowing: z.boolean(),
});

export function parseSessionUser(user: unknown) {
  const parseResult = userSessionSchema.safeParse(user);

  if (!parseResult.success) {
    return null;
  }

  return parseResult.data;
}

export function parseUserNotifications(notifications: unknown) {
  const parseResult = z.array(UserNotificationsSchema).safeParse(notifications);

  if (!parseResult.success) {
    console.error(parseResult.error);
  }

  return parseResult.success ? parseResult.data : null;
}

export type User = z.infer<typeof userSchema>;
export type UserSession = z.infer<typeof userSessionSchema>;

const userFollowersSchema = z.object({
  followers: z.array(
    z
      .object({
        follower: userSchema
          .pick({
            name: true,
            image: true,
          })
          .extend({
            id: z.string().cuid(),
          }),
      })
      .transform((val) => val.follower),
  ),
});

export type UserFollowers = z.infer<typeof userFollowersSchema>;

export const parseUserFollowers = (userWithFollowers: unknown) => {
  const parseResult = userFollowersSchema.safeParse(userWithFollowers);

  return parseResult.success ? parseResult.data : null;
};

const userFollowersAndSubscribersSchema = userFollowersSchema
  .extend({
    subscription: z
      .object({
        createdSubscriptions: z.array(
          z.object({
            subscribers: z.array(
              z.object({
                user: userSchema
                  .pick({
                    name: true,
                    image: true,
                  })
                  .extend({
                    id: z.string().cuid(),
                  }),
              }),
            ),
          }),
        ),
      })
      .nullish(),
  })
  .transform((val) => {
    return {
      followers: val.followers,
      subscribers:
        val.subscription?.createdSubscriptions.flatMap((subscription) =>
          subscription.subscribers.flatMap((subscriber) => subscriber.user),
        ) ?? [],
    };
  });

export type UserFollowersAndSubscribers = z.infer<
  typeof userFollowersAndSubscribersSchema
>;

export const parseUserFollowersAndSubscribers = (user: unknown) => {
  const parseResult = userFollowersAndSubscribersSchema.safeParse(user);

  return parseResult.success ? parseResult.data : null;
};

export const emailLoginSchema = z.object({
  email: z.string().email(),
});

export const profileFormSchema = z.object({
  name: z
    .string()
    .min(2, {
      message: "Username must be at least 2 characters.",
    })
    .max(30, {
      message: "Username must not be longer than 30 characters.",
    }),

  bio: z
    .string()
    .max(2000, {
      message: "Bio must not be longer than 2000 characters.",
    })
    .default(""),
  urls: z
    .array(
      z.object({
        value: z.string().url({ message: "Please enter a valid URL." }),
      }),
    )
    .default([]),
  addresses: z
    .array(
      z.object({
        value: z
          .string()
          .refine((val) => validate(val, TETHER_SYMBOL.toLowerCase())),
      }),
    )
    .default([]),
});

export const termsSchema = z.object({
  termsAccepted: z.boolean().refine((val) => val, {
    message: "Please accept the terms of service.",
  }),
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;

export type TermsFormValues = z.infer<typeof termsSchema>;
